// Static seed data for the tasks-v2 redesign. No network, no services.

import type {
	Profile,
	Project,
	Sprint,
	Stage,
	TaskType,
	Task,
} from "./types";

export const STAGES: Stage[] = [
	{ key: "backlog", name: "Backlog", color: "#64748b", position: 0 },
	{ key: "todo", name: "To Do", color: "#0058be", position: 1 },
	{ key: "in-progress", name: "In Progress", color: "#f59e0b", position: 2 },
	{ key: "qa", name: "QA", color: "#7c3aed", position: 3 },
	{ key: "done", name: "Done", color: "#006c49", position: 4 },
	{ key: "cancelled", name: "Cancelled", color: "#94a3b8", position: 5 },
];

// Board hides the cancelled column, matching the live page.
export const BOARD_STAGES = STAGES.filter((s) => s.key !== "cancelled");

export const PROFILES: Profile[] = [
	{
		id: "u1",
		full_name: "Ava Reyes",
		email: "ava@taskhub.dev",
		avatar_url: null,
	},
	{
		id: "u2",
		full_name: "Marcus Lee",
		email: "marcus@taskhub.dev",
		avatar_url: null,
	},
	{
		id: "u3",
		full_name: "Priya Nair",
		email: "priya@taskhub.dev",
		avatar_url: null,
	},
	{
		id: "u4",
		full_name: "Diego Santos",
		email: "diego@taskhub.dev",
		avatar_url: null,
	},
	{
		id: "u5",
		full_name: "Hana Kim",
		email: "hana@taskhub.dev",
		avatar_url: null,
	},
];

export const PROJECTS: Project[] = [
	{ id: "p1", name: "Orion Web App", color: "#0058be" },
	{ id: "p2", name: "Nova Mobile", color: "#006c49" },
	{ id: "p3", name: "Atlas Platform", color: "#7c3aed" },
];

export const SPRINTS: Sprint[] = [
	{
		id: "s1",
		name: "Sprint 24",
		status: "active",
		start_date: "2026-07-14",
		end_date: "2026-07-28",
	},
	{
		id: "s2",
		name: "Sprint 25",
		status: "planned",
		start_date: "2026-07-28",
		end_date: "2026-08-11",
	},
];

export const TASK_TYPES: TaskType[] = [
	{ id: "t1", name: "Feature", color: "#0058be", icon: "sparkles" },
	{ id: "t2", name: "Bug", color: "#dc2626", icon: "bug" },
	{ id: "t3", name: "Chore", color: "#64748b", icon: "wrench" },
	{ id: "t4", name: "Design", color: "#7c3aed", icon: "palette" },
];

const P = (id: string) => PROFILES.find((p) => p.id === id) as Profile;

function daysFromNow(n: number): string {
	const d = new Date("2026-07-21T00:00:00Z");
	d.setDate(d.getDate() + n);
	return d.toISOString().slice(0, 10);
}

export const TASKS: Task[] = [
	{
		id: "TASK-1",
		project_id: "p1",
		ticket_code: "ORN-101",
		title: "Redesign the task board layout",
		description:
			"Explore a cleaner column layout with a right slide-over detail drawer.",
		status: "in-progress",
		sub_status: null,
		priority: "high",
		assigned_to: P("u1"),
		additional_assignees: [P("u3")],
		created_by: P("u2"),
		due_date: daysFromNow(3),
		tags: ["frontend", "design"],
		estimated_time: 480,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t4",
		has_calendar_event: true,
		github: {
			branch_name: "feat/board-redesign",
			pr_state: "open",
			pr_url: "#",
			pr_number: 482,
		},
		checklist: [
			{ id: "c1", label: "Audit current board", done: true },
			{ id: "c2", label: "Draft column spec", done: true },
			{ id: "c3", label: "Build card component", done: false },
			{ id: "c4", label: "Wire detail drawer", done: false },
		],
		comments: [
			{
				id: "cm1",
				author: P("u2"),
				body: "Let's keep the priority border but soften the shadow.",
				created_at: daysFromNow(-1),
			},
			{
				id: "cm2",
				author: P("u1"),
				body: "Agreed. Slide-over feels lighter than the modal.",
				created_at: daysFromNow(0),
			},
		],
		history: [
			{
				id: "h1",
				actor: P("u2"),
				summary: "created the task",
				created_at: daysFromNow(-4),
			},
			{
				id: "h2",
				actor: P("u1"),
				summary: "moved from To Do to In Progress",
				created_at: daysFromNow(-1),
			},
		],
		attachments: [
			{
				id: "a1",
				file_name: "board-mockup.png",
				mime_type: "image/png",
				file_size: 248000,
				created_at: daysFromNow(-1),
			},
		],
		created_at: daysFromNow(-4),
		updated_at: daysFromNow(0),
	},
	{
		id: "TASK-2",
		project_id: "p1",
		ticket_code: "ORN-102",
		title: "Fix avatar overflow on card footer",
		description: "Avatars overlap the due date on narrow columns.",
		status: "todo",
		sub_status: null,
		priority: "medium",
		assigned_to: P("u3"),
		additional_assignees: [],
		created_by: P("u1"),
		due_date: daysFromNow(5),
		tags: ["bug"],
		estimated_time: 60,
		sprint_id: "s1",
		parent_task_id: "TASK-1",
		task_type_id: "t2",
		has_calendar_event: false,
		github: null,
		checklist: [],
		comments: [],
		history: [
			{
				id: "h3",
				actor: P("u1"),
				summary: "created the task",
				created_at: daysFromNow(-2),
			},
		],
		attachments: [],
		created_at: daysFromNow(-2),
		updated_at: daysFromNow(-2),
	},
	{
		id: "TASK-3",
		project_id: "p2",
		ticket_code: "NOV-210",
		title: "Offline mode for task sync",
		description: "Queue mutations while offline and replay on reconnect.",
		status: "backlog",
		sub_status: null,
		priority: "urgent",
		assigned_to: P("u4"),
		additional_assignees: [P("u5")],
		created_by: P("u4"),
		due_date: daysFromNow(12),
		tags: ["mobile", "sync"],
		estimated_time: 960,
		sprint_id: "s2",
		parent_task_id: null,
		task_type_id: "t1",
		has_calendar_event: false,
		github: null,
		checklist: [
			{ id: "c5", label: "Define queue schema", done: false },
			{ id: "c6", label: "Conflict strategy", done: false },
		],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-6),
		updated_at: daysFromNow(-6),
	},
	{
		id: "TASK-4",
		project_id: "p2",
		ticket_code: "NOV-211",
		title: "Push notification permission prompt",
		description: null,
		status: "qa",
		sub_status: "Ready for review",
		priority: "low",
		assigned_to: P("u5"),
		additional_assignees: [],
		created_by: P("u4"),
		due_date: daysFromNow(1),
		tags: ["mobile"],
		estimated_time: 120,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t1",
		has_calendar_event: true,
		github: {
			branch_name: "feat/push-prompt",
			pr_state: "merged",
			pr_url: "#",
			pr_number: 470,
		},
		checklist: [
			{ id: "c7", label: "iOS prompt", done: true },
			{ id: "c8", label: "Android prompt", done: true },
			{ id: "c9", label: "QA on devices", done: false },
		],
		comments: [
			{
				id: "cm3",
				author: P("u5"),
				body: "Merged, moving to QA for device testing.",
				created_at: daysFromNow(-1),
			},
		],
		history: [
			{
				id: "h4",
				actor: P("u5"),
				summary: "opened pull request #470",
				created_at: daysFromNow(-2),
			},
		],
		attachments: [],
		created_at: daysFromNow(-5),
		updated_at: daysFromNow(-1),
	},
	{
		id: "TASK-5",
		project_id: "p3",
		ticket_code: "ATL-330",
		title: "Migrate auth to new session service",
		description: "Replace legacy token handling with the session service.",
		status: "in-progress",
		sub_status: null,
		priority: "high",
		assigned_to: P("u2"),
		additional_assignees: [P("u1"), P("u4")],
		created_by: P("u2"),
		due_date: daysFromNow(4),
		tags: ["backend", "auth"],
		estimated_time: 600,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t1",
		has_calendar_event: false,
		github: {
			branch_name: "chore/session-migrate",
			pr_state: "draft",
			pr_url: "#",
			pr_number: 488,
		},
		checklist: [],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-3),
		updated_at: daysFromNow(0),
	},
	{
		id: "TASK-6",
		project_id: "p3",
		ticket_code: "ATL-331",
		title: "Add rate limiting to public API",
		description: "Protect the public endpoints with per-key rate limits.",
		status: "done",
		sub_status: null,
		priority: "medium",
		assigned_to: P("u4"),
		additional_assignees: [],
		created_by: P("u2"),
		due_date: daysFromNow(-2),
		tags: ["backend"],
		estimated_time: 180,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t3",
		has_calendar_event: false,
		github: null,
		checklist: [{ id: "c10", label: "Load test", done: true }],
		comments: [],
		history: [
			{
				id: "h5",
				actor: P("u4"),
				summary: "marked the task done",
				created_at: daysFromNow(-1),
			},
		],
		attachments: [],
		created_at: daysFromNow(-8),
		updated_at: daysFromNow(-1),
	},
	{
		id: "TASK-7",
		project_id: "p1",
		ticket_code: "ORN-103",
		title: "Empty state illustration for board",
		description: "Show a friendly empty state when a column has no tasks.",
		status: "todo",
		sub_status: null,
		priority: "low",
		assigned_to: P("u3"),
		additional_assignees: [],
		created_by: P("u1"),
		due_date: null,
		tags: ["design"],
		estimated_time: 90,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t4",
		has_calendar_event: false,
		github: null,
		checklist: [],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-1),
		updated_at: daysFromNow(-1),
	},
	{
		id: "TASK-8",
		project_id: "p2",
		ticket_code: "NOV-212",
		title: "Crash on cold start (Android 13)",
		description: "App crashes on first launch after install on Android 13.",
		status: "in-progress",
		sub_status: null,
		priority: "urgent",
		assigned_to: P("u5"),
		additional_assignees: [P("u4")],
		created_by: P("u5"),
		due_date: daysFromNow(0),
		tags: ["bug", "mobile"],
		estimated_time: 240,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t2",
		has_calendar_event: false,
		github: null,
		checklist: [
			{ id: "c11", label: "Reproduce", done: true },
			{ id: "c12", label: "Root cause", done: false },
		],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-1),
		updated_at: daysFromNow(0),
	},
	{
		id: "TASK-9",
		project_id: "p1",
		ticket_code: "ORN-104",
		title: "Keyboard shortcuts for board navigation",
		description: null,
		status: "backlog",
		sub_status: null,
		priority: "low",
		assigned_to: null,
		additional_assignees: [],
		created_by: P("u1"),
		due_date: null,
		tags: [],
		estimated_time: 0,
		sprint_id: null,
		parent_task_id: null,
		task_type_id: "t1",
		has_calendar_event: false,
		github: null,
		checklist: [],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-7),
		updated_at: daysFromNow(-7),
	},
	{
		id: "TASK-10",
		project_id: "p3",
		ticket_code: "ATL-332",
		title: "Audit log export to CSV",
		description: "Allow admins to export the audit log for a date range.",
		status: "qa",
		sub_status: null,
		priority: "medium",
		assigned_to: P("u2"),
		additional_assignees: [],
		created_by: P("u4"),
		due_date: daysFromNow(2),
		tags: ["backend", "admin"],
		estimated_time: 150,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t1",
		has_calendar_event: false,
		github: null,
		checklist: [],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-4),
		updated_at: daysFromNow(-1),
	},
	{
		id: "TASK-11",
		project_id: "p2",
		ticket_code: "NOV-213",
		title: "Dark mode polish pass",
		description: "Fix contrast issues across the mobile app in dark mode.",
		status: "done",
		sub_status: null,
		priority: "low",
		assigned_to: P("u3"),
		additional_assignees: [P("u5")],
		created_by: P("u3"),
		due_date: daysFromNow(-5),
		tags: ["design", "mobile"],
		estimated_time: 300,
		sprint_id: "s1",
		parent_task_id: null,
		task_type_id: "t4",
		has_calendar_event: false,
		github: null,
		checklist: [
			{ id: "c13", label: "Tokens", done: true },
			{ id: "c14", label: "Screens", done: true },
		],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-10),
		updated_at: daysFromNow(-5),
	},
	{
		id: "TASK-12",
		project_id: "p1",
		ticket_code: "ORN-105",
		title: "Deprecated filter helper cleanup",
		description: "Remove dead filter helpers after pagination landed.",
		status: "cancelled",
		sub_status: null,
		priority: "low",
		assigned_to: P("u1"),
		additional_assignees: [],
		created_by: P("u2"),
		due_date: null,
		tags: [],
		estimated_time: 30,
		sprint_id: null,
		parent_task_id: null,
		task_type_id: "t3",
		has_calendar_event: false,
		github: null,
		checklist: [],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-9),
		updated_at: daysFromNow(-3),
	},
	{
		id: "TASK-13",
		project_id: "p3",
		ticket_code: "ATL-333",
		title: "Webhook retries with backoff",
		description: "Retry failed webhook deliveries with exponential backoff.",
		status: "todo",
		sub_status: null,
		priority: "high",
		assigned_to: P("u4"),
		additional_assignees: [],
		created_by: P("u2"),
		due_date: daysFromNow(7),
		tags: ["backend"],
		estimated_time: 210,
		sprint_id: "s2",
		parent_task_id: null,
		task_type_id: "t1",
		has_calendar_event: false,
		github: null,
		checklist: [],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-2),
		updated_at: daysFromNow(-2),
	},
	{
		id: "TASK-14",
		project_id: "p2",
		ticket_code: "NOV-214",
		title: "Onboarding tooltip sequence",
		description: "Guide new users through the first three screens.",
		status: "backlog",
		sub_status: null,
		priority: "medium",
		assigned_to: P("u5"),
		additional_assignees: [P("u3")],
		created_by: P("u5"),
		due_date: null,
		tags: ["mobile", "design"],
		estimated_time: 240,
		sprint_id: null,
		parent_task_id: null,
		task_type_id: "t4",
		has_calendar_event: false,
		github: null,
		checklist: [],
		comments: [],
		history: [],
		attachments: [],
		created_at: daysFromNow(-3),
		updated_at: daysFromNow(-3),
	},
];
